// Routing puro de los dominios consumer (redytoeat.com + hosts dedicados). Sin env, sin IO — el proxy solo ejecuta la decisión.
// Spec: docs/superpowers/specs/2026-08-09-marketplace-redytoeat-analisis.md §2-3.
import { SLUG_REGEX, isReservedSlug } from "@/lib/slug/validateSlug"

export const CONSUMER_APEX = "redytoeat.com"
// Hosts dedicados: dominio propio de UN negocio, sirviendo su storefront en la
// raíz (hoy: dallasvibra.com → /vibra, para el alta de North de Dallas Vibra).
// La raíz reescribe al storefront del negocio y la URL se queda en su dominio;
// su slug colapsa a "/" (un solo canónico en el host), los slugs de OTROS
// negocios van al marketplace canónico, y el canonical SEO no cambia
// (storefrontUrl, consumer-sitemap y consumer-robots siguen emitiendo
// CONSUMER_APEX — el host dedicado no compite como contenido duplicado).
// Alta de un host nuevo = agregarlo aquí + dominio en el proyecto de Vercel +
// DNS. La exención de CSP (sin ella el checkout de North muere al pagar) ya no
// se toca: desde 2026-09-28 next.config.ts aplica la CSP solo a la casa
// merchant (MERCHANT_HOST_RE) y todo otro host queda exento.
const DEDICATED_HOSTS: Record<string, string> = { "dallasvibra.com": "vibra" }
const CONSUMER_APEXES = new Set([CONSUMER_APEX, ...Object.keys(DEDICATED_HOSTS)])
const CONSUMER_HOSTS = new Set([...CONSUMER_APEXES].flatMap((a) => [a, `www.${a}`]))
const MERCHANT_BASE = "https://useredy.com"

// Lo que la propia página consumer necesita del mismo origen. favicon/manifest/sw/icons
// NO están aquí: el matcher del proxy ya los excluye antes de llegar (ver
// `export const config` al final de proxy.ts, patrón en :445).
const PASS_PREFIXES = ["/api/", "/_next/", "/receipt/", "/r/"]
const PASS_EXACT = new Set(["/api", "/receipt", "/r", "/opengraph-image", "/icon.png", "/apple-icon.png"])

/**
 * Favicon, apple-touch-icon y manifest del storefront (app/order/[slug]/icon,
 * apple-icon y manifest.webmanifest). La página los enlaza con su ruta LARGA
 * (`/order/<slug>/icon?<hash>`) y el navegador los pide tal cual: tienen que
 * servirse ahí. Pasarlos por el 308 «forma larga → corta» los mandaba a
 * `/<slug>/icon`, que no es tienda, de ahí a la casa merchant y a /login — el
 * navegador recibía HTML y la pestaña se quedaba sin ícono (medido con curl el
 * 2026-10-07 en redytoeat.com).
 */
const STOREFRONT_ASSET_RE = /^\/order\/[^/]+\/(icon|apple-icon|manifest\.webmanifest)$/
export function isStorefrontAssetPath(pathname: string): boolean {
  return STOREFRONT_ASSET_RE.test(pathname)
}

// Raíces que viven SOLO en el apex consumer: la cuenta (cookie host-only) y el
// seguimiento del pedido (/p/<código>). En redytoeat pasan al app tal cual; en
// un host dedicado de un negocio y en useredy.com de producción se mandan al apex.
const CONSUMER_ONLY_ROOTS = ["/cuenta", "/p"]
export function isConsumerOnlyPath(pathname: string): boolean {
  return CONSUMER_ONLY_ROOTS.some((root) => pathname === root || pathname.startsWith(`${root}/`))
}

export type ConsumerRouteDecision =
  | { kind: "pass" }
  | { kind: "rewrite"; pathname: string }
  | { kind: "redirect"; location: string; status: 308 }

export function isConsumerHost(host: string): boolean {
  return CONSUMER_HOSTS.has(host.split(":")[0].toLowerCase())
}

export function resolveConsumerRoute(host: string, pathname: string): ConsumerRouteDecision {
  const bare = host.split(":")[0].toLowerCase()
  if (!CONSUMER_HOSTS.has(bare)) return { kind: "pass" }
  // Cada host consumer es su propio apex: www.X → X y los redirects internos
  // (forma larga → corta) se quedan en X. Un visitante de un host dedicado solo
  // cambia de dominio si pide un negocio que no vive ahí.
  const apex = bare.startsWith("www.") ? bare.slice("www.".length) : bare
  if (bare !== apex) return { kind: "redirect", location: `https://${apex}${pathname}`, status: 308 }
  const dedicated = DEDICATED_HOSTS[apex] // undefined = marketplace completo (redytoeat)

  if (pathname === "/" || pathname === "") {
    return { kind: "rewrite", pathname: dedicated ? `/order/${dedicated}` : "/order" }
  }
  if (pathname === "/order") return { kind: "redirect", location: `https://${apex}/`, status: 308 }
  if (isStorefrontAssetPath(pathname)) return { kind: "pass" }
  if (pathname.startsWith("/order/")) {
    const rest = pathname.slice("/order".length)
    if (!dedicated) return { kind: "redirect", location: `https://${apex}${rest}`, status: 308 }
    if (rest === `/${dedicated}`) return { kind: "redirect", location: `https://${apex}/`, status: 308 }
    if (rest.startsWith(`/${dedicated}/`)) return { kind: "redirect", location: `https://${apex}${rest}`, status: 308 }
    return { kind: "redirect", location: `https://${CONSUMER_APEX}${rest}`, status: 308 }
  }

  // SEO files por host (las rutas /consumer-* las crea F1b)
  if (pathname === "/sitemap.xml") return { kind: "rewrite", pathname: "/consumer-sitemap.xml" }
  if (pathname === "/robots.txt") return { kind: "rewrite", pathname: "/consumer-robots.txt" }

  // App del cliente (spec §4.7): el manifest del POS (app/manifest.ts) no puede
  // ser lo que instala un comensal desde redytoeat. Mismo patrón que sitemap/robots.
  if (pathname === "/manifest.webmanifest") return { kind: "rewrite", pathname: "/consumer-manifest.webmanifest" }

  // Cuenta y seguimiento: van ANTES del parseo de slugs porque, sin esto,
  // "/cuenta" es un slug de negocio y se reescribe a /order/cuenta → 404
  // (medido con curl el 2026-09-03).
  if (isConsumerOnlyPath(pathname)) {
    return dedicated
      ? { kind: "redirect", location: `https://${CONSUMER_APEX}${pathname}`, status: 308 }
      : { kind: "pass" }
  }

  if (PASS_EXACT.has(pathname) || PASS_PREFIXES.some((p) => pathname.startsWith(p))) return { kind: "pass" }
  if (pathname.includes(".")) return { kind: "pass" }   // assets sueltos (foto.jpg, *.txt de verificación)

  const segments = pathname.split("/").filter(Boolean)
  const slugOk = (s: string) => SLUG_REGEX.test(s) && !isReservedSlug(s)
  const isSingleSlug = segments.length === 1 && slugOk(segments[0])
  const isMesa =
    segments.length === 3 && segments[1] === "mesa" && slugOk(segments[0]) && segments[2].length > 0

  if (dedicated) {
    // Host de UN negocio: su slug colapsa a la raíz, su mesa sirve aquí, y
    // cualquier otro negocio vive en el marketplace canónico.
    if (isSingleSlug && segments[0] === dedicated) {
      return { kind: "redirect", location: `https://${apex}/`, status: 308 }
    }
    if (isMesa && segments[0] === dedicated) return { kind: "rewrite", pathname: `/order${pathname}` }
    if (isSingleSlug || isMesa) {
      return { kind: "redirect", location: `https://${CONSUMER_APEX}${pathname}`, status: 308 }
    }
  } else {
    if (isSingleSlug) return { kind: "rewrite", pathname: `/order/${segments[0]}` }
    if (isMesa) return { kind: "rewrite", pathname: `/order${pathname}` }
  }

  // Marketing, auth, admin, sub-rutas de slug, basura → la casa merchant decide (incl. su 404)
  return { kind: "redirect", location: `${MERCHANT_BASE}${pathname}`, status: 308 }
}

/**
 * A dónde vuelve el cliente de un host consumer tras pagar de paso en
 * useredy.com (lib/microsite/hostedPayment.ts): la forma CORTA del storefront
 * en el host del que salió. null si ese host no sirve ese negocio — así la
 * página de pago nunca manda a un host arbitrario.
 */
export function consumerStorefrontReturnUrl(host: string, slug: string): string | null {
  const bare = host.split(":")[0].toLowerCase()
  if (!CONSUMER_HOSTS.has(bare)) return null
  const apex = bare.startsWith("www.") ? bare.slice("www.".length) : bare
  const dedicated = DEDICATED_HOSTS[apex]
  if (dedicated) return dedicated === slug ? `https://${apex}/` : null
  return SLUG_REGEX.test(slug) && !isReservedSlug(slug) ? `https://${apex}/${slug}` : null
}

export function resolveLegacyOrderRedirect(host: string, pathname: string, live: boolean): string | null {
  if (!live) return null
  const bare = host.split(":")[0].toLowerCase()
  if (bare !== "useredy.com" && bare !== "www.useredy.com") return null
  if (pathname !== "/order" && !pathname.startsWith("/order/")) return null
  // Pago "de paso" de un «Dominio propio» (lib/microsite/hostedPayment.ts): TIENE
  // que correr en useredy.com, que es el dominio registrado en North. En
  // redytoeat esta ruta ni existe (caería al 308 de vuelta a useredy: bucle).
  if (/^\/order\/[^/]+\/pagar\/?$/.test(pathname)) return null
  // Y los íconos/manifest de esa misma página de pago: se sirven aquí, no en
  // redytoeat (allí la forma corta de un ícono no existe).
  if (isStorefrontAssetPath(pathname)) return null
  const rest = pathname.slice("/order".length)
  return `https://${CONSUMER_APEX}${rest === "" ? "/" : rest}`
}

/**
 * En el dominio merchant de PRODUCCIÓN, /cuenta* se manda al apex consumer:
 * la cuenta es una sola y su cookie es host-only. Sin flag a propósito — el
 * link de claim que acuña el servidor ya apunta a redytoeat en producción
 * (accountBaseUrl), así que aquí solo aterriza quien tecleó la URL a mano.
 * localhost y *.vercel.app devuelven null: en dev/preview la cuenta se prueba
 * en el mismo host, porque el apex consumer no resuelve a ese deploy.
 */
export function resolveAccountHostRedirect(host: string, pathname: string): string | null {
  const bare = host.split(":")[0].toLowerCase()
  if (bare !== "useredy.com" && bare !== "www.useredy.com") return null
  if (!isConsumerOnlyPath(pathname)) return null
  return `https://${CONSUMER_APEX}${pathname}`
}
